"use client";
import { useEffect } from "react";
import { LayoutGrid, Search } from "lucide-react";
import Link from "next/link";
import SearchSuggestionsEmpty from "./SearchSuggestionsEmpty";
import useSearch from "@/app/lib/features/search/search.hook";
import useDebounce from "@/app/hooks/useDebounce";
import SearchSuggestionsSkeleton from "../../../skeletons/SearchSuggestionsSkeleton";
import FallbackImage from "../../../FallbackImage";
import { trackCategoryClick, trackBrandClick, trackSellerView } from "@/app/lib/tracking/interactions";

interface SearchSuggestionsProps {
  query: string;
  onClose: () => void;
  onSelect: (term: string) => void;
}

const MIN_CHARS = 2;

const typeLabels: Record<string, string> = {
  search: "Arama",
  category: "Kategori",
  brand: "Marka",
  seller: "Mağaza",
};

const getHref = (type: string, query: string, slug: string): string => {
  if (type === "search") return `/aramasonucu?q=${encodeURIComponent(query)}`;
  if (type === "seller") return `/magaza/${slug}`;
  return `/${slug}`;
};

const SearchSuggestions = ({
  query,
  onClose,
  onSelect,
}: SearchSuggestionsProps) => {
  const {
    suggestions,
    isSuggestionsLoading,
    fetchSuggestions,
    resetSuggestions,
    saveSearch,
  } = useSearch();

  const debouncedQuery = useDebounce(query, 500);

  useEffect(() => {
    if (debouncedQuery.length >= MIN_CHARS) {
      fetchSuggestions({ search: debouncedQuery });
    } else {
      resetSuggestions();
    }
  }, [debouncedQuery, fetchSuggestions, resetSuggestions]);

  if (query.length < MIN_CHARS) return null;

  if (isSuggestionsLoading || debouncedQuery.length < MIN_CHARS) {
    return <SearchSuggestionsSkeleton />;
  }

  if (!suggestions) return null;

  const searchSuggestions = suggestions.suggestions ?? [];
  const categories = suggestions.categories ?? [];
  const brands = suggestions.brands ?? [];
  const sellers = suggestions.sellers ?? [];

  const isEmpty =
    searchSuggestions.length === 0 &&
    categories.length === 0 &&
    brands.length === 0 &&
    sellers.length === 0;

  if (isEmpty) {
    return <SearchSuggestionsEmpty query={query} />;
  }

  const groups = [
    {
      key: "category",
      title: "Kategoriler",
      items: categories.map((c) => ({
        id: `cat-${c.id}`,
        entityId: c.id,
        title: c.title,
        type: "category" as const,
        query: c.title,
        slug: c.slug,
        imageUrl: "",
      })),
    },
    {
      key: "search",
      title: "Arama Önerileri",
      items: searchSuggestions.map((s) => ({
        id: `search-${s.query}`,
        entityId: 0,
        title: s.title,
        type: "search" as const,
        query: s.query,
        slug: "",
        imageUrl: "",
      })),
    },
    {
      key: "brand",
      title: "Markalar",
      items: brands.map((b) => ({
        id: `brand-${b.id}`,
        entityId: b.id,
        title: b.title,
        type: "brand" as const,
        query: b.title,
        slug: b.slug,
        imageUrl: b.image_url ?? "",
      })),
    },
    {
      key: "seller",
      title: "Mağazalar",
      items: sellers.map((s) => ({
        id: `seller-${s.id}`,
        entityId: s.id,
        title: s.title,
        type: "seller" as const,
        query: s.title,
        slug: s.slug,
        imageUrl: s.logo ?? "",
      })),
    },
  ].filter((g) => g.items.length > 0);

  const handleClick = (type: string, itemQuery: string, entityId: number) => {
    if (type === "search") {
      saveSearch({ search_term: itemQuery });
    } else if (type === "category") {
      trackCategoryClick(entityId);
    } else if (type === "brand") {
      trackBrandClick(entityId);
    } else if (type === "seller") {
      trackSellerView(entityId);
    }

    // Tür ne olursa olsun arama kutusunda tıklanan öneri yazsın. Önceden
    // yalnızca "search" tipinde yazılıyordu; kategori/marka/mağaza seçilince
    // kullanıcının yazdığı metin ("erkek") kutuda kalıyordu.
    onSelect(itemQuery);
    // setValue dropdown'ı açtığı için close sonra çağrılmalı.
    onClose();
  };

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key}>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
            {group.title}
          </p>
          <div>
            {group.items.map((item) => {
              const href = getHref(item.type, item.query, item.slug);
              const showImage = item.type === "seller" || item.type === "brand";

              return (
                <Link
                  key={item.id}
                  href={href}
                  onClick={() => handleClick(item.type, item.query, item.entityId)}
                  className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md hover:bg-muted transition-colors group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {showImage ? (
                      <div className="w-7 h-7 rounded-full shrink-0 overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                        <FallbackImage
                          src={item.imageUrl}
                          alt={item.title}
                          width={28}
                          height={28}
                          className="w-full h-full object-contain"
                          fallbackClassName="w-4 h-4 object-contain opacity-50 m-auto"
                        />
                      </div>
                    ) : item.type === "category" ? (
                      <div className="w-7 h-7 rounded-full shrink-0 bg-blue-50 flex items-center justify-center">
                        <LayoutGrid size={13} className="text-blue-500" />
                      </div>
                    ) : (
                      <div className="w-7 h-7 rounded-full shrink-0 bg-gray-100 flex items-center justify-center">
                        <Search size={13} className="text-muted-foreground" />
                      </div>
                    )}
                    <span className="text-sm truncate">{item.title}</span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground bg-muted group-hover:bg-background px-1.5 py-0.5 rounded">
                    {typeLabels[item.type]}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SearchSuggestions;
